import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const HEARTBEAT_MS = 30_000;

export const useActivityTracker = () => {
  const { user, profile, roles } = useAuth();
  const location = useLocation();
  const lastPathRef = useRef<string>("");
  const lastPathStartRef = useRef<number>(Date.now());

  // Resolve nursery name once we have nursery_id
  const nurseryNameRef = useRef<string | null>(null);
  useEffect(() => {
    if (!profile?.nursery_id) {
      nurseryNameRef.current = null;
      return;
    }
    supabase
      .from("nurseries")
      .select("name")
      .eq("id", profile.nursery_id)
      .maybeSingle()
      .then(({ data }) => {
        nurseryNameRef.current = (data as any)?.name ?? null;
      });
  }, [profile?.nursery_id]);

  // Heartbeat + path change handler
  useEffect(() => {
    if (!user) return;

    const path = location.pathname + location.search;
    const role = roles[0] || "user";
    const userName = profile?.full_name || user.email?.split("@")[0] || "مستخدم";

    const upsertSession = async () => {
      try {
        await supabase
          .from("user_activity_sessions")
          .upsert(
            {
              user_id: user.id,
              nursery_id: profile?.nursery_id ?? null,
              nursery_name: nurseryNameRef.current,
              user_name: userName,
              user_role: role,
              current_path: path,
              last_seen_at: new Date().toISOString(),
              user_agent: navigator.userAgent.slice(0, 200),
            },
            { onConflict: "user_id" }
          );
      } catch {
        /* ignore */
      }
    };

    // Log previous path duration on path change
    const logPrevious = async () => {
      if (!lastPathRef.current) return;
      const duration = Math.floor((Date.now() - lastPathStartRef.current) / 1000);
      if (duration < 2) return;
      try {
        await supabase.from("user_activity_log").insert({
          user_id: user.id,
          nursery_id: profile?.nursery_id ?? null,
          path: lastPathRef.current,
          duration_seconds: duration,
        });
      } catch {
        /* ignore */
      }
    };

    if (lastPathRef.current && lastPathRef.current !== path) {
      logPrevious();
    }
    lastPathRef.current = path;
    lastPathStartRef.current = Date.now();

    upsertSession();
    const interval = setInterval(upsertSession, HEARTBEAT_MS);

    const onVisibility = () => {
      if (document.visibilityState === "visible") upsertSession();
    };
    document.addEventListener("visibilitychange", onVisibility);

    const onUnload = () => {
      try {
        navigator.sendBeacon?.(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_activity_log`,
          new Blob(
            [
              JSON.stringify({
                user_id: user.id,
                nursery_id: profile?.nursery_id ?? null,
                path,
                duration_seconds: Math.floor((Date.now() - lastPathStartRef.current) / 1000),
              }),
            ],
            { type: "application/json" }
          )
        );
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("beforeunload", onUnload);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [user, profile?.nursery_id, location.pathname, location.search, roles, profile?.full_name]);
};
